import { useMemo } from 'react'

import type {
  FlightPlayerScoresFragment,
  HoleScoreFragment,
} from '../../../__generated__/hooks'
import type { CurrentPlayerCtx } from '../../../PlayerShell'
import { isNetStrokes } from '../../../util/event-mode'
import SideSummary from '../components/SideSummary'
import SideTotalRow from '../components/SideTotalRow'
import Table from '../components/Table'

const FORMAT_LABEL: Record<string, string> = {
  SCRAMBLE: 'Scramble',
  BESTBALL: 'Best ball',
  FOURSOME: 'Foursome',
  GOLF: 'Singel',
}

type Block = {
  ordinal: number
  fromHole: number
  toHole: number
  format: string
}

/**
 * A split round's card: one small card per block, stacked.
 *
 * NOT one 18-row grid. A grid asserts a single hole × entrant coordinate
 * system, and this round has three — the team plays the one-ball block, the
 * players play the rest — so two thirds of its cells are structurally empty.
 * Those blanks are not missing data, and the app already spends `—` on
 * "a stroke here, not played yet", so the grid says every team has twelve
 * holes left and every player six. Neither is true.
 *
 * Stacked cards rather than a block switcher, even though the leaderboard uses
 * one: a leaderboard compares standings within a scope, where one scope at a
 * time is the point, but a card is a document you read end to end to check it.
 * Hiding two thirds of it behind tabs makes the one job this screen exists for
 * a three-tap operation, and you can never see that the card is complete.
 *
 * Every block card shows what was RECORDED, not what counts. A pair records 30
 * — six team rows and twelve per player — of which 24 count, because best ball
 * keeps one ball of two. Drawing only the counting ball would leave nothing to
 * check the card against.
 */
/**
 * The pairs among a block's player entries, in the order they arrive. Insertion
 * order, so the viewer's own side keeps the lead the flight ordering gave it.
 */
const sidesIn = (
  entries: FlightPlayerScoresFragment[]
): { id: string; name: string; entries: FlightPlayerScoresFragment[] }[] => {
  const sides = new Map<
    string,
    { id: string; name: string; entries: FlightPlayerScoresFragment[] }
  >()
  for (const entry of entries) {
    const id = entry.team?.id
    if (!id) continue
    const side = sides.get(id) ?? {
      id,
      name: entry.team?.name ?? 'Uten navn',
      entries: [],
    }
    side.entries.push(entry)
    sides.set(id, side)
  }
  return [...sides.values()]
}

const SegmentedView = ({
  player,
  flight,
  holes,
}: {
  player: CurrentPlayerCtx
  flight: FlightPlayerScoresFragment[]
  holes: HoleScoreFragment[]
}) => {
  const showStableford = !isNetStrokes(player.polfEvent.ranking)
  const blocks: Block[] = useMemo(
    () =>
      [...(player.polfEvent.segments ?? [])].sort(
        (a, b) => a.fromHole - b.fromHole
      ),
    [player.polfEvent.segments]
  )

  const yoursFirst = (entries: FlightPlayerScoresFragment[]) =>
    [...entries].sort((a, b) => {
      const yours = (id: string) => id === player.id || id === player.team?.id
      if (yours(a.id)) return -1
      if (yours(b.id)) return 1
      return 0
    })

  return (
    <section className="pb-6">
      <div className="px-4 pt-4 pb-2 flex items-end justify-between">
        <h1 className="font-display text-xl font-bold text-green-900">
          Scorekort
        </h1>
        <p className="text-xs text-ink-500 num">
          {holes.length} hull · {blocks.length} etapper
        </p>
      </div>

      <div className="flex flex-col gap-4">
        {blocks.map((block) => {
          const blockHoles = holes.filter(
            (h) =>
              h.hole.number >= block.fromHole && h.hole.number <= block.toHole
          )
          // Who owns a row in THIS block. One entry kind per card, which is
          // what removes the blanks.
          const ordered = yoursFirst(
            flight.filter((entry) =>
              (entry.scores ?? []).some(
                (s) =>
                  s.hole.number >= block.fromHole &&
                  s.hole.number <= block.toHole
              )
            )
          )
          if (ordered.length === 0 || blockHoles.length === 0) return null

          // Grouped by side, not merely sorted by it: the best-ball card spans
          // a cell across each pair, and a span assumes its columns are
          // adjacent. Sides keep the order `yoursFirst` gave them, and the
          // grouping is dropped whole if any entry is sideless — a card with a
          // column missing is worse than one whose pairs are apart.
          const sides = sidesIn(ordered)
          const grouped = sides.flatMap((side) => side.entries)
          const entries = grouped.length === ordered.length ? grouped : ordered

          const entriesAreTeams = entries.every((e) => e.isTeamEntry)

          // Best ball keeps one ball of two, and which one is the block's
          // whole output — an unmarked counting score is a scoring error, not
          // a presentational one. teamScores holds the picks for the entry's
          // side, scoped to this block by the API.
          const countingRowIds = new Set(
            block.format === 'BESTBALL'
              ? entries.flatMap((entry) =>
                  (entry.teamScores ?? []).map((s) => s.id)
                )
              : []
          )
          const cellSubLabel =
            block.format === 'BESTBALL'
              ? (entryId: string, holeNumber: number) => {
                  const entry = entries.find((e) => e.id === entryId)
                  const row = (entry?.scores ?? []).find(
                    (s) => s.hole.number === holeNumber
                  )
                  return row && countingRowIds.has(row.id) ? 'Teller' : null
                }
              : undefined

          return (
            <div key={block.ordinal} className="flex flex-col">
              <div className="px-4 pb-1 flex items-baseline gap-2">
                <span className="text-xs font-bold uppercase tracking-wider text-green-900">
                  {FORMAT_LABEL[block.format] ?? block.format}
                </span>
                <span className="font-mono num text-[11px] text-ink-500">
                  hull {block.fromHole}–{block.toHole}
                </span>
              </div>
              <Table
                player={player}
                flight={entries}
                holes={blockHoles}
                showStableford={showStableford}
                entriesAreTeams={entriesAreTeams}
                cellSubLabel={cellSubLabel}
                emphasizeTotal={block.format !== 'BESTBALL'}
                sideTotalRow={
                  // Best ball is the one etappe where the columns and the
                  // result are different entrants: four players are drawn, two
                  // sides are playing.
                  block.format === 'BESTBALL' && entries === grouped ? (
                    <SideTotalRow
                      sides={sides}
                      block={block}
                      showStableford={showStableford}
                    />
                  ) : null
                }
              />
            </div>
          )
        })}
      </div>

      <SideSummary blocks={blocks} flight={flight} player={player} />
    </section>
  )
}

export default SegmentedView
